import { Commit, ActionTree, Dispatch } from 'vuex';
import axiosInstance from '@/services/api/http.service.js';
import getEnv from '@/utils/env';
import { State } from './configure.store';
import * as types from '../../apm/mutation-types';
import { CapabilityType } from './config-constant';
import graphQl from '../../../../services/graphQl';
import axios, { AxiosResponse } from 'axios';
import { ResponseWrapper } from '@/services/api/utils';
import { parseServiceObjFromApplication } from '@/utils/helper-function';
import AlertsV2Utils from '@/components/AlertsV2/alertsV2Utils';
import { dotNetLang } from '@/components/APM/Dashboard/Instance/instance.constants';
import { ANOMALY_COUNT, ANOMALY_COUNT_LABEL } from '@/components/AlertsV2/constants';
import { permissionTypes } from '@/router/constant';

const API_URL =
  getEnv('VUE_APP_ALERT_HOST') ||
  process.env.VUE_APP_ALERT_HOST ||
  'http://localhost:63250';

const http = new axiosInstance({ auth: true });

const nginxInstanceTypeMapping = {
  'open source': 'Open',
  plus: 'Plus',
};

const formatAggregationNames = function (aggreElement) {
  if (aggreElement === 'AVG') return 'Average';
  if (aggreElement === 'DYNAMIC') return 'Dynamic baseline';
  return aggreElement;
};

const getInstanceType = function(capability, context, subType) {
  try {
    if (capability === 'V') return subType === 'W' ? 'Windows' : 'Linux';
    if (capability === 'W')
      return nginxInstanceTypeMapping[
        context.rootState.wsmState.detail.currentSubType.value
      ];
    if (
      capability === 'A' &&
      context.rootState.apmState.apmCommonState.currentServiceLanguage ===
        dotNetLang
    )
      return 'Dotnet';
  } catch (error) {
    // TODO: Do something with error
  }

  return null;
};

const actions: ActionTree<State, any> = {
  async getAlertRules(
    context: { commit: Commit },
    params: {
      keyword: String;
      serviceId: String;
      limit: Number;
      from: Number;
      sortBy: String;
      order: String;
    },
  ) {
    try {
      if (params.serviceId === '') {
        context.commit('setRules', []);
        context.commit('setTotalRules', 0);
        context.commit('setSearchTotalRules', 0);
        context.commit('setIsSearchedAlarmQuery', false);
        return;
      }
      context.commit('setRules', []);
      // context.commit('setSearchTotalRules', 0);
      context.commit('setTotalRules', 0);
      context.commit('setIsSearchedAlarmQuery', false);
      const response = await http.post(
        `${API_URL}/api/v1/rules/configured`,
        params,
      );
      if (response && response.data && response.data.success == true) {
        let rulesList = response.data.data.rules.map((rule) => {
          const { alertRule, ...meta } = rule;
          return { ...alertRule, ...meta };
        });
        context.commit('setRules', rulesList);
        context.commit('setSearchTotalRules', response.data.data.total);
        context.commit('setTotalRules', response.data.data.total);
        if (params.keyword || params.keyword != '') {
          context.commit('setIsSearchedAlarmQuery', true);
        }
      }
      throw response;
    } catch (error) {
      // Handle error
    }
  },
  async updateAlertRule(
    context: { commit: Commit },
    params: {
      ruleId: String;
      active: Boolean;
    },
  ) {
    if (!params) {
      return new Promise<void>((resolve) => resolve());
    }
    const response = await http.post(`${API_URL}/api/v1/rules/`, params);
    if (response && response.data && response.data.success == true) {
      return response;
    }
    throw response;
  },

  async deleteAlertRule(context: { commit: Commit }, ruleId: String) {
    if (!ruleId) {
      return new Promise<void>((resolve) => resolve());
    }
    try {
      const response = await http.delete(`${API_URL}/api/v1/rules/${ruleId}`);
      return response;
    } catch (error) {
      //ToDo
    }
  },
  async reloadConfiguredAlerts(
    context: { state: State; commit: Commit },
    params: boolean,
  ) {
    context.commit('reloadConfiguredAlerts', params);
  },
  async GET_ALERT_APPLICATIONS(
    context: {
      commit: Commit;
      state: State;
    },
    param: any
  ) {
    let capability = context.state.currentCapability;
    let subType = context.state.currentSubType || '';
    const currentService = context.state.currentApplication;
    const alertApplication =
      param?.permissions?.[0] === permissionTypes.ALERT_WRITE
        ? context.state.alertApplicationsWithWritePermission
        : context.state.alertApplication;
    let params = {
      serviceReq: {
        capability: CapabilityType.get(capability.key),
      },
      duration: param.duration,
      keyword: param.keyword,
      pagination: param.pagination,
      permissions: param.permissions,
    };
    if (capability.key === 'SM') {
      params.serviceReq['subType'] = 'EP';
    }

    if (capability.key == 'CAPABILITY_CLOUD') {
      params.serviceReq['subType'] = subType;
    }

    if (capability.key === 'LOG' || capability.key === 'ANALYTICS') {
      params.serviceReq['subType'] = 'PUSHQ';
    }
    await graphQl
      .query('queryServicesWithTotalCount')  //TODO: need to update graphql query
      .params(params)
      .then((res: AxiosResponse) => {
        let _filteredServices: Array<Object> = [];
        let services = res?.data?.data?.services?.services;
        services =
          params.pagination.pageNum === 1
            ? services
            : [...alertApplication, ...services];
        const serviceCount = res?.data?.data?.services?.total ?? 0;
        if (Array.isArray(services) && services.length > 0) {
          services.forEach(service => {
            let serviceDetails = parseServiceObjFromApplication(service);
            if (
              serviceDetails.type === 'D' &&
              serviceDetails.sub !== 'MS' &&
              serviceDetails.sub !== 'MY' &&
              serviceDetails.sub !== 'OR' &&
              serviceDetails.sub !== 'CB' &&
              serviceDetails.sub !== 'PG' &&
              serviceDetails.sub !== 'MG'
            ) {
              return;
            }
            if (serviceDetails.type === 'S') {
              if (serviceDetails.sub === 'EP') {
                service.label = 'Group > ' + service.label;
              } else {
                // service.label = 'Application > ' + service.label;
                return;
              }
            }
            _filteredServices.push(service);
          });
          if (!currentService.key) {
            context.commit(
              'setCurrentAlertApplication',
              _filteredServices ? _filteredServices[0] : {},
            );
          }
        } else {
          context.commit('setCurrentAlertApplication', {
            key: '',
            label: '',
            permissions: [],
          });
        }
        if (param.permissions[0] === permissionTypes.ALERT_WRITE) {
          context.commit(
            'setAlertApplicationsWithWritePermissionCount',
            serviceCount,
          );
          context.commit(
            'setAlertApplicationsWithWritePermission',
            _filteredServices,
          );
        } else {
          context.commit('setAlertApplicationCount', serviceCount);
          context.commit('setAlertApplication', _filteredServices);
        }
      })
      .catch((error) => error);
  },
  async GET_ENDPOINTS(
    context: { dispatch: Dispatch; commit: Commit; state: State },
    params: any
  ) {
    const prevEndpoints = context.state.endpoints;
    return graphQl
      .query('querySMEndpoint')
      .params(params)
      .then((res: AxiosResponse) => {
        if (res && res.data && res.data.data) {
          let endpoints = res.data.data.getEndpoints?.endpoints;
          const endpointCount = res.data.data.getEndpoints?.total ?? 0;
          endpoints =
            params.pagination.pageNum === 1
              ? endpoints
              : [...prevEndpoints, ...endpoints];
          context.commit('setEndpoints', endpoints);
          context.commit('setEndpointCount', endpointCount);
        }
      });
  },
  async GET_INSTANCES(
    context: { dispatch: Dispatch; commit: Commit },
    params: any,
  ) {
    return graphQl
      .query('queryServiceInstances')
      .params(params)
      .then((res: AxiosResponse) => {
        if (res && res.data && res.data.data) {
          context.commit('setInstances', res.data.data.instanceId);
          return res.data.data.instanceId;
        } else return [];
      });
  },
  async GET_INSTANCES_WITH_COUNT(
    context: { dispatch: Dispatch; commit: Commit, state: State },
    params: any
  ) {
    const prevInstances = context.state.instances;
    return graphQl
      .query('queryServiceInstancesWithTotalCount')
      .params(params)
      .then((res: AxiosResponse) => {
        if (res && res.data && res.data.data) {
          let instancesIds = res.data.data?.instanceId?.serviceInstances;
          const instancesCount = res.data.data?.instanceId?.total ?? 0;
          instancesIds =
            params.pagination.pageNum === 1
              ? instancesIds
              : [...prevInstances, ...instancesIds];
          context.commit('setInstances', instancesIds);
          context.commit('setInstanceCount', instancesCount);
          return res.data.data?.instanceId;
        } else return { serviceInstances: [], total: 0 };
      });
  },
  async GET_INSTANCE_BY_META(context: { dispatch: Dispatch; commit: Commit },
    params: any
  ) {
    return graphQl
      .query('queryServiceInstanceByMeta')
      .params(params)
      .then((res: AxiosResponse) => {
        if (res && res.data && res.data.data) return res.data.data;
      });
  },
  async GET_NOTIFICATION_CHANNELS(context: { commit: Commit }) {
    try {
      const response = await http.get(`${API_URL}/api/v1/channels/names`);
      if (response && response.data && response.data.success == true) {
        context.commit('setNotificationsChannels', response.data.data);
      }
      throw response;
    } catch (error) {
      // Handle error
    }
  },
  async GET_METRICS_MAP(
    context: { commit: Commit },
    params: {
      capability: string;
      serviceId: string;
    },
  ) {
    try {
      const subType = await parseServiceObjFromApplication({
        key: params.serviceId,
        label: '',
      })?.sub;
      const instanceType = getInstanceType(params.capability, context, subType);
      const request = {
        capability: params.capability,
        sub: subType === "null" ? null : subType,
        type: instanceType,
      };
      const response = await http.post(`${API_URL}/api/v1/metrics/`, request);
      context.commit('setMetricsMap', []);
      context.commit('setAggregationMap', []);
      if (response && response.data && response.data.success == true) {
        let _metricData: object[] = [];
        let _aggregationData: object[] = [];
        context.commit('setMetricsMap', _metricData);
        context.commit('setAggregationMap', _aggregationData);
        // let _obj = response.data.data;
        const metricsToExclude =
          subType === 'RDS_MSSQL'
            ? ['rds_swap_usage', 'rds_bin_log_disk_usage']
            : subType === 'RDS_POSTGRES'
              ? ['rds_bin_log_disk_usage']
              : [];
        const _obj = response.data.data.filter(
          (element: any) => !metricsToExclude.includes(element.name),
        );
        _obj.forEach((element) => {
          let __tempObj = {
            key: element.name,
            label: element.displayName,
            isInstanceType: element.isInstanceType,
            instances: element.instances,
            filters : element.filters,
            category: element.category,
            aggregation: element.aggregation,
          };
          let _tempAggreDate: object[] = [];
          element.aggregation.forEach((aggreElement) => {
            _tempAggreDate.push({
              key: aggreElement,
              label: aggreElement === ANOMALY_COUNT 
                ? ANOMALY_COUNT_LABEL 
                : AlertsV2Utils.toTitleCase(aggreElement === 'AVG' ? 'Average' : aggreElement)
            });
          });
          let _mappedAggredate = {
            metric: element.name,
            metricLabel: element.displayName,
            aggregation: _tempAggreDate,
            unit: element.unit,
            min: element.min,
            max: element.max,
          };
          _aggregationData.push(_mappedAggredate);
          _metricData.push(__tempObj);
        });
        context.commit('setMetricsMap', _metricData);
        context.commit('setAggregationMap', _aggregationData);
        return new ResponseWrapper(response, response.data);
      }
      throw response;
    } catch (error) {
      // Handle error
      console.log('error', error);
      context.commit('setMetricsMap', []);
      context.commit('setAggregationMap', []);
    }
  },

  async GET_ALERT_ALL_METRICS_MAP(context: { commit: Commit }) {
    try {
      const response = await http.get(`${API_URL}/api/v1/metrics/`);
      if (response && response.data && response.data.success == true) {
        let _metricData: object[] = [];
        context.commit('setMetricsMap', _metricData);
        let _obj = response.data.data;
        _obj.forEach((element) => {
          var __tempObj = {
            key: element.name,
            label: element.displayName,
          };
          _metricData.push(__tempObj);
        });
        context.commit('setMetricsMap', _metricData);
        return new ResponseWrapper(response, response.data);
      }
      throw response;
    } catch (error) {
      // Handle error
    }
  },
  async SAVE_ALERT_RULE(
    context: { commit: Commit },
    params: { alarmRequest: any },
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const response = http.post(`${API_URL}/api/v1/rules/`, params);
        if (response && response.data && response.data.success == true) {
          //
        }
        resolve(response);
      } catch (error) {
        reject(error);
        // Handle error
      }
    });
  },
  async SEARCH_EXISTING_ALERT_RULE_NAME(
    context: { commit: Commit },
    params: { ruleName: string; serviceId: string },
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const response = http.post(`${API_URL}/api/v1/rules/search`, {
          ruleName: params.ruleName,
          serviceId: params.serviceId,
        });
        if (response && response.data && response.data.success == true) {
          //
        }
        resolve(response);
      } catch (error) {
        reject(error);
        // Handle error
      }
    });
  },
  async GET_TAGGED_NOTIFICATION_CHANELLS(
    context: { commit: Commit },
    channels: {}[],
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const response = http.post(
          `${API_URL}/api/v1/channels/tagged-search`,
          channels,
        );
        if (response && response.data && response.data.success == true) {
          //
        }
        resolve(response);
      } catch (error) {
        reject(error);
        // Handle error
      }
    });
  },
};

export default actions;
